import QrCode2Icon from '@mui/icons-material/QrCode2';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { StatusScreen } from '@mercadopago/sdk-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import InputMask from 'react-input-mask';

import { auth } from 'services/firebase/client';
import { createPixPayment, getPaymentStatus } from 'services/paymentService';

const PIX_POLL_INTERVAL_MS = 5000;

interface PixPaymentPanelStyles {
  card: object;
  paymentHeader: object;
  icon: object;
  advanceButton: object;
}

interface PixSessionData {
  paymentId: string;
  contextKey: string;
}

interface PixPaymentPanelProps {
  total: number;
  cpf: string;
  email?: string;
  description: string;
  cartItems?: unknown[];
  isSubscriber?: boolean;
  storageKey: string;
  /** Identifica plano/pedido + valor; ao mudar, invalida PIX em cache */
  contextKey: string;
  disabled?: boolean;
  onPaymentApproved: (paymentId: string | number) => Promise<void>;
  onBeforeCreatePix?: () => Promise<void>;
  styles: PixPaymentPanelStyles;
}

const loadPixSession = (storageKey: string): PixSessionData | null => {
  const raw = sessionStorage.getItem(storageKey);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PixSessionData;
    if (parsed.paymentId && parsed.contextKey) {
      return parsed;
    }
  } catch {
    // Formato antigo: só o paymentId como string
    if (/^\d+$/.test(raw)) {
      return { paymentId: raw, contextKey: '' };
    }
  }
  return null;
};

const savePixSession = (storageKey: string, data: PixSessionData) => {
  sessionStorage.setItem(storageKey, JSON.stringify(data));
};

const clearPixSession = (storageKey: string) => {
  sessionStorage.removeItem(storageKey);
};

const PixPaymentPanel = ({
  total,
  cpf: initialCpf,
  email: emailProp,
  description,
  cartItems = [],
  isSubscriber = false,
  storageKey,
  contextKey,
  disabled = false,
  onPaymentApproved,
  onBeforeCreatePix,
  styles,
}: PixPaymentPanelProps) => {
  const [cpf, setCpf] = useState(initialCpf || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [pixPaymentId, setPixPaymentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const orderFinalizedRef = useRef(false);

  const resetPixState = useCallback(() => {
    clearPixSession(storageKey);
    setPixPaymentId(null);
    setError(null);
    orderFinalizedRef.current = false;
  }, [storageKey]);

  useEffect(() => {
    if (initialCpf && !cpf) {
      setCpf(initialCpf);
    }
  }, [initialCpf, cpf]);

  useEffect(() => {
    const session = loadPixSession(storageKey);
    if (session && session.contextKey === contextKey) {
      setPixPaymentId(session.paymentId);
      return;
    }
    resetPixState();
  }, [contextKey, resetPixState, storageKey]);

  const sanitizeCpf = (value: string) => value.replace(/\D/g, '');

  const handleApproved = useCallback(
    async (paymentId: string | number) => {
      if (orderFinalizedRef.current) {
        return;
      }
      orderFinalizedRef.current = true;
      setIsConfirming(true);
      await onPaymentApproved(paymentId);
      clearPixSession(storageKey);
    },
    [onPaymentApproved, storageKey],
  );

  const handleGeneratePix = async () => {
    const cpfDigits = sanitizeCpf(cpf);
    if (cpfDigits.length !== 11) {
      setError('Informe um CPF válido com 11 dígitos.');
      return;
    }

    const email = emailProp || auth.currentUser?.email;
    if (!email) {
      setError('E-mail não encontrado. Faça login novamente.');
      return;
    }

    setError(null);
    setIsGenerating(true);

    try {
      if (onBeforeCreatePix) {
        await onBeforeCreatePix();
      }

      const response = await createPixPayment({
        amount: total,
        payer: {
          email,
          identification: {
            type: 'CPF',
            number: cpfDigits,
          },
        },
        cartItems,
        isSubscriber,
        description,
      });

      const paymentId = String(response.id ?? response.payment_id);
      if (!paymentId) {
        throw new Error('Pagamento PIX criado sem identificador');
      }

      setPixPaymentId(paymentId);
      savePixSession(storageKey, { paymentId, contextKey });

      if (response.status === 'approved') {
        await handleApproved(paymentId);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Erro ao gerar PIX. Tente novamente.';
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!pixPaymentId || orderFinalizedRef.current) {
      return undefined;
    }

    const pollPaymentStatus = async () => {
      try {
        const statusResponse = await getPaymentStatus(pixPaymentId);

        if (statusResponse.status === 'approved') {
          await handleApproved(pixPaymentId);
        } else if (
          statusResponse.status === 'rejected' ||
          statusResponse.status === 'cancelled'
        ) {
          setError('Pagamento PIX recusado ou cancelado. Gere um novo código.');
          resetPixState();
        }
      } catch {
        // Mantém polling em falhas transitórias
      }
    };

    const intervalId = window.setInterval(() => {
      void pollPaymentStatus();
    }, PIX_POLL_INTERVAL_MS);

    void pollPaymentStatus();

    return () => {
      window.clearInterval(intervalId);
    };
  }, [handleApproved, pixPaymentId, resetPixState, storageKey]);

  if (pixPaymentId) {
    return (
      <Box>
        <Card
          sx={{ ...styles.card, mx: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 } }}
        >
          <CardContent>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Escaneie o QR Code ou copie o código PIX
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              O pagamento é confirmado automaticamente após a transferência.
            </Typography>
            {isConfirming && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2">
                  Pagamento recebido! Finalizando...
                </Typography>
              </Box>
            )}
            <StatusScreen
              key={`${contextKey}-${pixPaymentId}`}
              initialization={{ paymentId: pixPaymentId }}
              locale="pt-BR"
            />
          </CardContent>
        </Card>
        {error && (
          <Alert severity="error" sx={{ mx: { xs: 1, sm: 2 }, mt: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={resetPixState}
            disabled={isConfirming}
          >
            Gerar novo PIX
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Card
        sx={{ ...styles.card, mx: { xs: 1, sm: 2 }, px: { xs: 2, sm: 3 } }}
      >
        <CardContent sx={styles.paymentHeader}>
          <QrCode2Icon sx={styles.icon} />
          <Typography variant="body1">Pagar com PIX</Typography>
        </CardContent>
      </Card>

      <Card sx={{ ...styles.card, mx: { xs: 1, sm: 2 }, mt: 2, p: 2 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe seu CPF para gerar o QR Code. Valor:{' '}
            <strong>R$ {total.toFixed(2).replace('.', ',')}</strong>.
          </Typography>

          <InputMask
            mask="999.999.999-99"
            value={cpf}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCpf(e.target.value)
            }
            disabled={disabled || isGenerating}
          >
            {(inputProps: object) => (
              <TextField
                {...inputProps}
                fullWidth
                label="CPF"
                margin="normal"
                required
              />
            )}
          </InputMask>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            sx={{ ...styles.advanceButton, mt: 3 }}
            onClick={() => void handleGeneratePix()}
            disabled={disabled || isGenerating}
          >
            {isGenerating ? (
              <CircularProgress size={22} sx={{ color: '#fff' }} />
            ) : (
              'Gerar PIX'
            )}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PixPaymentPanel;
